"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../../../../lib/utils";
import Loader from "../../../../icons/normal/loader";

export type ButtonStyleProps = {
  variant: "transparent" | "filled" | "outline";
  state: "secondary" | "primary" | "cta" | "calendar";
};

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-sp40 whitespace-nowrap rounded ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:bg-semantic-disabled-background-global-default disabled:text-semantic-disabled-foreground-global-default [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      state: {
        primary: "",
        secondary: "",
        cta: "",
        calendar: "px-0 py-sp40",
      },
      variant: {
        filled:
          "disabled:bg-semantic-disabled-background-global-default disabled:text-semantic-disabled-foreground-global-default",
        outline: "disabled:border-semantic-disabled-foreground-global-default",
        transparent: "disabled:bg-transparent",
      },
      size: {
        xsm: "h-6 typography-semantic-label-2-medium",
        sm: "h-7 typography-semantic-label-1-normal---medium",
        md: "h-8 typography-semantic-body-1-normal---regular",
        lg: "h-9 typography-semantic-body-1-normal---medium",
        xlg: "h-10 typography-semantic-body-1-normal---medium",
        "icon-xsm": "h-6 w-6 p-0 [&_svg]:size-3",
        "icon-sm": "h-7 w-7 p-0 [&_svg]:size-3",
        "icon-md": "h-8 w-8 p-0 [&_svg]:size-4",
        "icon-lg": "h-9 w-9 p-0 [&_svg]:size-4",
        "icon-xlg": "h-10 w-10 p-0 [&_svg]:size-5",
      },
    },
    compoundVariants: [
      // Primary Button Variants
      {
        state: "primary",
        variant: "filled",
        className:
          "px-sp80 bg-semantic-primary-background-normal-default hover:bg-semantic-primary-background-normal-hovered active:bg-semantic-primary-background-normal-pressed text-semantic-static-global-global-white",
      },
      {
        state: "primary",
        variant: "outline",
        className:
          "px-sp80 border border-semantic-primary-foreground-global-default bg-semantic-secondary-background-normal hover:bg-semantic-primary-background-strong-hovered active:bg-semantic-primary-background-strong-pressed text-semantic-primary-foreground-global-default active:text-semantic-primary-foreground-global-pressed",
      },
      {
        state: "primary",
        variant: "transparent",
        className:
          "px-sp80 bg-transparent hover:bg-semantic-secondary-background-normal-hovered active:bg-semantic-secondary-background-normal-pressed text-semantic-primary-foreground-global-default hover:text-semantic-primary-foreground-global-hovered active:text-semantic-primary-foreground-global-pressed",
      },
      // Secondary Button Variants
      {
        state: "secondary",
        variant: "filled",
        className:
          "px-sp80 bg-semantic-secondary-background-strong-default hover:bg-semantic-secondary-background-strong-hovered active:bg-semantic-secondary-background-strong-pressed text-semantic-secondary-foreground-heavy-default",
      },
      {
        state: "secondary",
        variant: "outline",
        className:
          "px-sp80 border border-semantic-secondary-stroke-strong-default active:border-semantic-secondary-stroke-normal-pressed bg-semantic-secondary-background-normal hover:bg-semantic-secondary-background-strong-hovered active:bg-semantic-secondary-background-normal-pressed text-semantic-secondary-foreground-heavy-default hover:text-semantic-secondary-foreground-normal-hover data-[state=active]:bg-semantic-secondary-background-normal-selected data-[state=active]:text-semantic-primary-foreground-normal-default data-[state=active]:border-semantic-primary-stroke-normal-default",
      },
      {
        state: "secondary",
        variant: "transparent",
        className:
          "px-sp80 bg-transparent hover:bg-semantic-secondary-background-normal-hovered active:bg-semantic-secondary-background-normal-pressed text-semantic-secondary-foreground-heavy-default",
      },
      // CTA Button Variants
      {
        state: "cta",
        variant: "filled",
        className:
          "px-sp80 bg-semantic-cta-background-normal-default hover:bg-semantic-cta-background-normal-hovered active:bg-semantic-cta-background-normal-pressed text-semantic-static-global-global-white",
      },
      {
        state: "cta",
        variant: "outline",
        className:
          "px-sp80 border border-semantic-cta-stroke-global-default bg-semantic-secondary-background-normal hover:bg-semantic-cta-background-strong-hovered active:bg-semantic-cta-background-strong-pressed text-semantic-cta-foreground-global-default active:text-semantic-cta-foreground-global-pressed",
      },
      {
        state: "cta",
        variant: "transparent",
        className:
          "px-sp80 bg-transparent hover:bg-semantic-cta-background-strong-hovered active:bg-semantic-cta-background-strong-pressed text-semantic-cta-foreground-global-default hover:text-semantic-cta-foreground-hover active:text-semantic-cta-foreground-pressed",
      },
      // Calendar Button Variants
      {
        state: "calendar",
        variant: "filled",
        className:
          "bg-semantic-secondary-background-strong-default hover:bg-semantic-secondary-background-strong-hovered active:bg-semantic-secondary-background-strong-pressed text-semantic-secondary-foreground-heavy-default",
      },
      {
        state: "calendar",
        variant: "outline",
        className:
          "border border-semantic-secondary-stroke-strong-default active:border-semantic-secondary-stroke-normal-pressed bg-semantic-secondary-background-normal hover:bg-semantic-secondary-background-strong-hovered active:bg-semantic-secondary-background-normal-pressed text-semantic-secondary-foreground-heavy-default hover:text-semantic-secondary-foreground-normal-hover",
      },
      {
        state: "calendar",
        variant: "transparent",
        className:
          "bg-transparent hover:bg-semantic-secondary-background-normal-hovered active:bg-semantic-secondary-background-normal-pressed text-semantic-secondary-foreground-heavy-default",
      },
    ],
    defaultVariants: {
      state: "primary",
      variant: "filled",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      state,
      variant,
      size,
      asChild = false,
      isLoading = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(buttonVariants({ state, variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? <Loader className="animate-spin" /> : children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
